import { useState } from "react";
import { Alert, Autocomplete, Box, Checkbox, Chip, TextField, Tooltip, Typography, createFilterOptions } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQueryClient } from "@tanstack/react-query";
import type { OrgUnitAddressGroup } from "../types";
import { useAddressBookApi, useAddressBookOrgUnitId } from "../context";

const NEW_PREFIX = "+ ";
const DEFAULT_GROUP_HINT = "Addresses without a group assigned are automatically added to the Default group.";
const filter = createFilterOptions<string>();

interface AddressGroupSelectProps {
  selectedGroups: string[];
  onChange: (groups: string[]) => void;
  disabled: boolean;
  availableGroups?: OrgUnitAddressGroup[];
}

export default function AddressGroupSelect({
  selectedGroups,
  onChange,
  disabled,
  availableGroups = [],
}: Readonly<AddressGroupSelectProps>) {
  const api = useAddressBookApi();
  const orgUnitId = useAddressBookOrgUnitId();
  const options = availableGroups.map((g) => g.name).sort((a, b) => a.localeCompare(b));
  const { mutate: createGroup } = api.useCreateOrgUnitAddressGroup();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Groups
        </Typography>
        <Tooltip title={DEFAULT_GROUP_HINT} placement="right" arrow>
          <Box
            component="span"
            tabIndex={0}
            role="img"
            aria-label={DEFAULT_GROUP_HINT}
            sx={{ display: "inline-flex", color: "text.secondary", cursor: "help" }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      </Box>
      <Autocomplete
        multiple
        freeSolo={!!orgUnitId}
        disableCloseOnSelect
        options={options}
        value={selectedGroups}
        filterOptions={(opts, params) => {
          const filtered = filter(opts, params);
          if (!orgUnitId) return filtered;
          const { inputValue } = params;
          const trimmed = inputValue.trim();
          // Show "Create ..." option if input doesn't match any existing option (case-insensitive)
          if (trimmed && !opts.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
            filtered.push(`${NEW_PREFIX}${trimmed}`);
          }
          return filtered;
        }}
        onChange={(_event, newValue) => {
          setError(null);
          // Extract actual names from "Create ..." options
          const processedValues = newValue.map((v) =>
            v.startsWith(NEW_PREFIX) ? v.slice(NEW_PREFIX.length).trim() : v.trim()
          ).filter(Boolean);
          // Normalize typed values to match existing option names (case-insensitive)
          const existingByLower = new Map(availableGroups.map((g) => [g.name.toLowerCase(), g.name]));
          const normalized = processedValues.map((v) => existingByLower.get(v.toLowerCase()) ?? v);
          const seen = new Set<string>();
          const unique = normalized.filter((v) => {
            const key = v.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          if (unique.length !== selectedGroups.length || unique.some((v, i) => v !== selectedGroups[i])) {
            const newGroups = unique.filter((name) => !existingByLower.has(name.toLowerCase()));
            if (newGroups.length > 0 && orgUnitId) {
              newGroups.forEach((groupName) => {
                createGroup(
                  { orgUnitId: Number(orgUnitId), groupName },
                  {
                    onError: (err) => {
                      // Check for 409 conflict (group already exists)
                      const status = (err as { status?: number }).status;
                      if (status === 409) {
                        setError(`Group "${groupName}" already exists`);
                      } else {
                        setError(`Failed to create group "${groupName}"`);
                      }
                      // Remove the failed group from the current selection (unique), not stale props
                      onChange(unique.filter((g) => g.toLowerCase() !== groupName.toLowerCase()));
                      // Refresh the dropdown list
                      queryClient.invalidateQueries({
                        queryKey: ["GET_ORG_UNIT_ADDRESS_GROUPS", orgUnitId],
                      });
                    },
                  }
                );
              });
            }
            onChange(unique);
          }
        }}
        readOnly={disabled}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props;
          const isCreateOption = option.startsWith(NEW_PREFIX);
          return (
            <li key={key} {...rest}>
              {isCreateOption ? (
                <Typography color="primary" sx={{ fontStyle: "italic" }}>
                  {option}
                </Typography>
              ) : (
                <>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  {option}
                </>
              )}
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                size="small"
                {...tagProps}
                onDelete={disabled ? undefined : tagProps.onDelete}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedGroups.length === 0 ? "Select or type to create..." : ""}
            size="small"
          />
        )}
      />
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
